import React, { useId } from 'react';
import { Image } from '../Image/Image';
import type { OverlayPosition } from '../Image/Image';
import { ArrowLink } from '../ArrowLink/ArrowLink';
import { StoreBadgeRow, STORE_BADGE_NAMES } from '../StoreBadgeLink/StoreBadgeLink';
import { blockAnchorAttributes } from '../../utils/blockAnchor';
import { hasCtaDestination } from '../../utils/ctaAction';
import type { CtaAction } from '../../utils/ctaAction';
import './AppDownload.scss';

export type AppDownloadMediaPosition = 'left' | 'right';

export type AppDownloadBackground = 'off-white' | 'white';

/**
 * Corner the QR code occupies when a background image is authored. `center` is
 * excluded because centering is reserved for the gradient treatment, which sizes
 * the QR larger and has no background image to sit in the corner of.
 */
export type AppDownloadQrPosition = Exclude<OverlayPosition, 'center'>;

const BACKGROUND_COLORS: Record<AppDownloadBackground, string> = {
  'off-white': 'var(--poly-color-background-offWhite)',
  white: 'var(--poly-color-background-white)',
};

const DEFAULT_BACKGROUND: AppDownloadBackground = 'off-white';

/**
 * Width of the QR overlay when it sits in a corner of an authored background image.
 * A fixed size, not a percentage: a QR has to stay large enough to scan, so the design
 * holds it at 128px from 1920 down to 360 rather than letting it shrink with the panel.
 *
 * Must also stay a literal length: `Image` applies this as an inline style, and the
 * CSSOM discards a `var()` expression on a standard property, which collapses the
 * absolutely-positioned overlay to zero width.
 */
const QR_WIDTH_CORNER = '128px';


const getBackgroundColor = (background?: AppDownloadBackground): string => {
  if (!background) return BACKGROUND_COLORS[DEFAULT_BACKGROUND];
  const color = BACKGROUND_COLORS[background];
  if (!color) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console -- surface CMS mis-config to authors during dev
      console.warn(
        `[AppDownload] Unknown background preset: "${background}". Falling back to "${DEFAULT_BACKGROUND}".`,
      );
    }
    return BACKGROUND_COLORS[DEFAULT_BACKGROUND];
  }
  return color;
};

/**
 * @polySemanticUsage About this block: an app-download section pairing a QR code with the official
 * App Store and Google Play badges. Use it wherever a page invites the reader to install the mobile
 * app. The store badges are supplied by the block — never author their text, links, or artwork. A
 * heading, a subheading, and a QR code image are required; upload a background photo to place the
 * QR in a corner of it, or leave the photo empty to get the built-in gradient with a larger,
 * centered QR.
 */
export interface AppDownloadProps {
  /**
   * Section background color.
   * @polyType SelectList
   * @polyOptions Off-White=off-white,White=white
   * @polyDefault off-white
   * @polyLabel Background
   * @polyTranslatable false
   */
  background?: AppDownloadBackground;

  /**
   * Position of the QR panel relative to the text content.
   * @polyType SelectList
   * @polyOptions Left=left,Right=right
   * @polyDefault right
   * @polyLabel Media Position
   * @polyTranslatable false
   */
  mediaPosition?: AppDownloadMediaPosition;

  /**
   * Section heading. Required.
   * @polyLabel Heading
   */
  heading: string;

  /**
   * Supporting copy below the heading. Required.
   * @polyLabel Subheading
   */
  subheading: string;

  /**
   * Optional short label above the heading.
   * @polyLabel Eyebrow
   */
  eyebrow?: string;

  /**
   * QR code image encoding the app listing link. Required.
   * @polyType Url
   * @polyLabel QR Code Image URL
   */
  qrCodeUrl: string;

  /**
   * QR code alt text. Leave empty to mark the QR decorative — the App Store and
   * Google Play badges beside it already give assistive-technology users a
   * labelled link to the same destination, so an unlabelled QR loses nothing.
   * @polyLabel QR Code Alt Text
   */
  qrCodeAlt?: string;

  /**
   * Corner the QR occupies over the background image. Ignored when no background
   * image is set, since the gradient treatment always centers the QR.
   * @polyType SelectList
   * @polyOptions Bottom Right=bottom-right,Bottom Left=bottom-left,Top Right=top-right,Top Left=top-left
   * @polyDefault bottom-right
   * @polyLabel QR Code Position
   * @polyTranslatable false
   */
  qrCodePosition?: AppDownloadQrPosition;

  /**
   * Background photo behind the QR code. Leaving this empty selects the built-in
   * gradient treatment, which centers the QR and renders it larger.
   * @polyType Url
   * @polyLabel Background Image URL
   */
  backgroundImageUrl?: string;

  /**
   * Background photo alt text. Leave empty for a decorative photo.
   * @polyLabel Background Image Alt Text
   */
  backgroundImageAlt?: string;

  /**
   * Image painted across the gradient panel, replacing the built-in gradient.
   * Only consulted for the gradient treatment: setting a background photo selects
   * the photo treatment instead, and that photo wins. The QR stays centered and
   * large either way, so supplying this changes the panel's artwork and nothing
   * about the layout. Rendered decoratively with empty alt text, since a gradient
   * carries no information a screen-reader user would otherwise miss.
   * @polyType Url
   * @polyLabel Gradient Image URL
   */
  gradientImageUrl?: string;

  /**
   * Link 1. A slot renders as an arrow-link below the store badges only when both its
   * text and its URL are set.
   * @polyLabel Link 1 Text
   */
  link1Text?: string;
  /**
   * @polyType Url
   * @polyLabel Link 1 URL
   */
  link1Href?: string;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the Link URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  link1Action?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  link1TargetPopupId?: string;

  /**
   * Link 2
   * @polyLabel Link 2 Text
   */
  link2Text?: string;
  /**
   * @polyType Url
   * @polyLabel Link 2 URL
   */
  link2Href?: string;
  /**
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the Link URL field.
   * @polyDefault link
   * @polyTranslatable false
   */
  link2Action?: CtaAction;
  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  link2TargetPopupId?: string;

  /**
   * ID prefix for the store badge links; each badge is suffixed with its store name.
   * @polyLabel Store Badges ID
   * @polyTranslatable false
   */
  storeBadgesId?: string;

  /**
   * data-ld-append value applied to both store badge links for LeadCode attribution.
   * @polyLabel Store Badges LD Appendix
   * @polyTranslatable false
   */
  storeBadgesLdAppendix?: string;
  /**
   * Anchor ID for in-page links: a link to `#<anchorId>` on the same page scrolls to this block.
   * Normalised to letters, numbers and dashes (spaces become dashes, a leading `#` and other
   * characters are dropped). Must be unique on the page. Migration: when any link on the source page points at `#<id>` and that id sits on the
   * source section this block replaces, copy the id here unchanged (without the `#`).
   * @polyType Text
   * @polyLabel Anchor ID
   * @polyHelp Lets in-page links such as #your-id jump to this block. Use letters, numbers and dashes (other characters are removed); must be unique on the page.
   * @polyTranslatable false
   */
  anchorId?: string;
}

export const AppDownload: React.FC<AppDownloadProps> = ({
  background,
  mediaPosition = 'right',
  heading,
  subheading,
  eyebrow,
  qrCodeUrl,
  qrCodeAlt = '',
  qrCodePosition = 'bottom-right',
  backgroundImageUrl,
  backgroundImageAlt = '',
  gradientImageUrl,
  link1Text,
  link1Href,
  link1Action,
  link1TargetPopupId,
  link2Text,
  link2Href,
  link2Action,
  link2TargetPopupId,
  storeBadgesId,
  storeBadgesLdAppendix,
  anchorId,
}) => {
  const missingRequired = (
    [
      [heading, 'Heading'],
      [subheading, 'Subheading'],
      [qrCodeUrl, 'QR Code Image URL'],
    ] as const
  )
    .filter(([value]) => !value)
    .map(([, label]) => label);

  if (missingRequired.length > 0 && process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console -- surface CMS mis-config to authors during dev
    console.warn(
      `[AppDownload] Missing required field(s): ${missingRequired.join(', ')}. The block renders incomplete until they are set.`,
    );
  }

  const headingId = useId();
  // The heading renders only when authored, so the landmark name is guarded with it —
  // an aria-labelledby pointing at an absent id names the section nothing at all.
  const labelledBy = heading ? headingId : undefined;

  const linkCandidates = [
    { text: link1Text, href: link1Href, action: link1Action, popupTargetId: link1TargetPopupId },
    { text: link2Text, href: link2Href, action: link2Action, popupTargetId: link2TargetPopupId },
  ];
  const links = linkCandidates.filter((link) => Boolean(
    link.text && hasCtaDestination(link.href, link.action, link.popupTargetId),
  ));

  const hasBackgroundImage = Boolean(backgroundImageUrl);

  const mediaSlot = hasBackgroundImage ? (
    <div className="app-download__media">
      <Image
        url={backgroundImageUrl as string}
        alt={backgroundImageAlt}
        objectFit="cover"
        overlay1Url={qrCodeUrl}
        overlay1Alt={qrCodeAlt}
        overlay1Position={qrCodePosition}
        overlay1Width={QR_WIDTH_CORNER}
      />
    </div>
  ) : (
    <div className="app-download__media app-download__media--gradient">
      {gradientImageUrl && (
        <Image
          bare
          url={gradientImageUrl}
          alt=""
          className="app-download__gradient-image"
        />
      )}
      {qrCodeUrl && (
        <Image bare url={qrCodeUrl} alt={qrCodeAlt} className="app-download__qr" />
      )}
    </div>
  );

  return (
    <section
      {...blockAnchorAttributes(anchorId)}
      className={`app-download app-download--media-${mediaPosition}`}
      style={{ backgroundColor: getBackgroundColor(background) }}
      aria-labelledby={labelledBy}
      data-poly-block="AppDownload"
      data-poly-error={missingRequired.length > 0 ? 'missing-required-fields' : undefined}
    >
      <div className="app-download__inner">
        <div className="app-download__text">
          {eyebrow && <span className="app-download__eyebrow">{eyebrow}</span>}
          {heading && (
            <h2 id={headingId} className="app-download__heading">
              {heading}
            </h2>
          )}
          {subheading && <p className="app-download__subheading">{subheading}</p>}

          <StoreBadgeRow
            stores={STORE_BADGE_NAMES}
            id={storeBadgesId}
            ldAppendix={storeBadgesLdAppendix}
            className="app-download__badges"
          />

          {links.length > 0 && (
            <div className="app-download__links">
              {links.map((link, i) => (
                <ArrowLink
                  key={`link-${i}`}
                  href={link.href}
                  action={link.action}
                  popupTargetId={link.popupTargetId}
                  direction="right"
                >
                  {link.text}
                </ArrowLink>
              ))}
            </div>
          )}
        </div>
        {mediaSlot}
      </div>
    </section>
  );
};
